import DeleteIcon from '@mui/icons-material/DeleteOutline';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { Box, Card, CardContent, CardHeader, Divider, IconButton, Stack } from '@mui/material';
import { useId } from 'react';
import { useDrag, useDrop } from 'react-dnd';

import { FormField, api, isDefined } from 'common';

import { ConditionCardValues } from './ConditionCardValues';
import { ConditionLabelField } from './ConditionLabelField';
import { DATA_TYPE_OPTIONS, getOperationLabel } from './constants';
import { useConditionLabel } from './useConditionLabel';
import { ItemType } from '../../../../../../../dragdrop';
import { BlockValueTemplateField } from '../../ConfigFields/CodeMirror/FlowTemplate';
import { BlockInnerSelectField } from '../../ConfigFields/SelectField';
import { useSelectedNodeField } from '../../ConfigFields/useSelectedNodeField';

interface ConditionCardProps {
  conditionId: string;
  index: number;
  fieldNamePrefix: string;
  onReorder: (fromIndex: number, toIndex: number) => void;
  onDelete: () => void;
}

interface DragItem {
  conditionId: string;
  index: number;
}

export function ConditionCard(props: ConditionCardProps) {
  const { conditionId, index, fieldNamePrefix, onReorder, onDelete } = props;
  const { value: condition, setValue: setCondition } =
    useSelectedNodeField<Partial<api.FlowDefinition.Conditional.ConditionalCondition>>(
      fieldNamePrefix
    );
  const { updateConditionLabel } = useConditionLabel({ fieldNamePrefix });
  const fieldId = useId();
  const { visibleError: dataTypeError } =
    useSelectedNodeField<api.FlowDefinition.Conditional.ConditionalFieldDataType>(
      `${fieldNamePrefix}.dataType`
    );
  const { visibleError: operationError } =
    useSelectedNodeField<api.FlowDefinition.Conditional.ConditionalOperation>(
      `${fieldNamePrefix}.operation`
    );

  const [{ isDragging }, dragRef] = useDrag({
    type: ItemType.ConditionalCondition,
    item: { conditionId, index },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const [{ canDrop, isOver }, dropRef] = useDrop({
    accept: ItemType.ConditionalCondition,
    drop: (item: DragItem) => {
      if (item.index !== index) {
        onReorder(item.index, index);
      }
    },
    collect: (monitor) => ({
      canDrop: monitor.canDrop(),
      isOver: monitor.isOver(),
    }),
  });

  if (!condition) {
    return null;
  }
  const { dataType, operation } = condition;

  return (
    <Card
      ref={(node) => {
        dropRef(node);
      }}
      variant="outlined"
      sx={{
        opacity: isDragging ? 0.5 : 1,
        border: isOver && canDrop ? '2px solid' : undefined,
        borderColor: isOver && canDrop ? 'primary.main' : undefined,
        cursor: isDragging ? 'grabbing' : 'default',
      }}
    >
      <CardHeader
        title="If"
        sx={{
          backgroundColor: '#FAFAFA',
          paddingBottom: 2,
        }}
        action={
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Box
              ref={(node: HTMLDivElement | null) => {
                dragRef(node);
              }}
              sx={{
                cursor: 'grab',
                display: 'flex',
                alignItems: 'center',
                color: 'text.secondary',
                '&:active': {
                  cursor: 'grabbing',
                },
              }}
            >
              <IconButton
                aria-label="Drag to reorder"
                size="small"
                sx={{ color: 'text.secondary' }}
              >
                <DragIndicatorIcon fontSize="small" />
              </IconButton>
            </Box>
            <IconButton
              aria-label="Delete Condition"
              size="small"
              onClick={() => {
                onDelete();
              }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Stack>
        }
      />
      <CardContent>
        <Stack gap={1}>
          <BlockValueTemplateField
            fieldName={`${fieldNamePrefix}.inputValue`}
            label="Input Value"
          />
          <FormField
            label="Data Type & Operation"
            id={fieldId}
            error={[dataTypeError, operationError].filter(isDefined).join(', ') || undefined}
          >
            <Stack
              direction="row"
              gap={0}
              flex="1"
              sx={{ border: '1px solid #BFBFBF', borderRadius: 1 }}
            >
              <Box
                flexBasis="200px"
                flexGrow={0}
                flexShrink={0}
                sx={{
                  backgroundColor: '#F7F7F7',
                  borderTopLeftRadius: (theme) => theme.shape.borderRadius,
                  borderBottomLeftRadius: (theme) => theme.shape.borderRadius,
                }}
              >
                <BlockInnerSelectField<api.FlowDefinition.Conditional.ConditionalFieldDataType>
                  fieldName={`${fieldNamePrefix}.dataType`}
                  options={DATA_TYPE_OPTIONS}
                  aria-label="Data Type"
                  clearable={false}
                  variant="borderless"
                  onChange={(event) => {
                    if (event.target.value) {
                      const newDataType = event.target.value;
                      let newOperation = operation;
                      if (
                        !isDefined(newOperation) ||
                        !api.FlowDefinition.Conditional.OPERATIONS_BY_SUPPORTED_DATA_TYPE[
                          newDataType
                        ].includes(newOperation)
                      ) {
                        newOperation =
                          api.FlowDefinition.Conditional.OPERATIONS_BY_SUPPORTED_DATA_TYPE[
                            newDataType
                          ]?.at(0);
                      }

                      setCondition({
                        id: condition.id,
                        inputValue: condition.inputValue,
                        dataType: newDataType,
                        operation: newOperation,
                      } as Partial<api.FlowDefinition.Conditional.ConditionalCondition>);
                    }
                  }}
                />
              </Box>
              <Divider orientation="vertical" flexItem />
              <Box flexBasis="auto" flexGrow={1} flexShrink={0}>
                <BlockInnerSelectField<api.FlowDefinition.Conditional.ConditionalOperation>
                  fieldName={`${fieldNamePrefix}.operation`}
                  clearable={false}
                  aria-label="Operation"
                  variant="borderless"
                  options={operationOptions(dataType, operation)}
                  onChange={(event) => {
                    updateConditionLabel({ operation: event.target.value });
                  }}
                />
              </Box>
            </Stack>
          </FormField>
          <Box>
            <ConditionCardValues fieldNamePrefix={fieldNamePrefix} />
          </Box>
          <ConditionLabelField fieldNamePrefix={fieldNamePrefix} />
        </Stack>
      </CardContent>
    </Card>
  );
}

function operationOptions(
  dataType?: api.FlowDefinition.Conditional.ConditionalFieldDataType,
  selectedOperation?: api.FlowDefinition.Conditional.ConditionalOperation
) {
  if (!dataType) {
    return [];
  }
  let operations = api.FlowDefinition.Conditional.OPERATIONS_BY_SUPPORTED_DATA_TYPE[dataType].map(
    (operation) => ({
      label: getOperationLabel({
        dataType,
        operation,
      } as api.FlowDefinition.Conditional.ConditionalCondition),
      value: operation,
    })
  );
  if (dataType === api.FlowDefinition.Conditional.ConditionalFieldDataType.DateTime) {
    operations = operations.filter((operation) => {
      switch (operation.value) {
        case api.FlowDefinition.Conditional.ConditionalOperation.Equals:
        case api.FlowDefinition.Conditional.ConditionalOperation.GreaterThan:
        case api.FlowDefinition.Conditional.ConditionalOperation.LessThan:
          return operation;
        default:
          // If the user has already selected an operation that is no longer being shown
          // (i.e., from a previous session with the older UI), show that option.
          // Otherwise, hide it from the list.
          return operation.value === selectedOperation ? operation : undefined;
      }
    });
  }
  return operations;
}
